import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useSupabaseUser } from "@/hooks/use-supabase-user";
import { UserPlus, Trash2, Users, Shield, Info } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface Contributor {
  id: string;
  contributor_admin_id: string;
  created_at: string;
  profiles: {
    full_name: string;
    email: string;
  };
}

export function ContributorManagement() {
  const { user } = useSupabaseUser();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newContributor, setNewContributor] = useState({
    email: "",
    password: "",
    firstName: "",
    lastName: "",
  });

  // Fetch contributors
  const { data: contributors, isLoading } = useQuery({
    queryKey: ["contributors", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("admin_contributors")
        .select("id, contributor_admin_id, created_at")
        .eq("parent_admin_id", user?.id);

      if (error) throw error;

      // Fetch profiles for contributors
      if (!data || data.length === 0) return [];

      const contributorIds = data.map(c => c.contributor_admin_id);
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .in("id", contributorIds);

      if (profilesError) throw profilesError;

      // Combine the data
      return data.map(contributor => ({
        ...contributor,
        profiles: profiles?.find(p => p.id === contributor.contributor_admin_id) || {
          full_name: "",
          email: "",
        },
      })) as Contributor[];
    },
    enabled: !!user?.id,
  });

  // Create contributor mutation
  const createContributorMutation = useMutation({
    mutationFn: async () => {
      console.log('🚀 Starting contributor creation...');
      
      // Use the create-user edge function
      // Don't set role='admin' because regular admins can't create admins
      // We'll add the admin role manually after creating the contributor relationship
      console.log('📞 Calling create-user edge function...');
      const { data, error } = await supabase.functions.invoke('create-user', {
        body: {
          email: newContributor.email,
          password: newContributor.password,
          fullName: `${newContributor.firstName} ${newContributor.lastName}`,
          firstName: newContributor.firstName,
          lastName: newContributor.lastName,
          // Don't set role here - will be added after contributor relationship
        },
      });

      console.log('📥 Edge function response:', { data, error });

      if (error) {
        console.error('❌ Edge function error:', error);
        throw error;
      }
      if (data?.error) {
        console.error('❌ Edge function returned error:', data.error);
        throw new Error(data.error);
      }
      
      const newUserId = data.user.id;
      console.log('✅ User created with ID:', newUserId);

      // Create contributor relationship
      console.log('📝 Creating contributor relationship...');
      const { error: contributorError } = await supabase
        .from("admin_contributors")
        .insert({
          parent_admin_id: user?.id,
          contributor_admin_id: newUserId,
          created_by: user?.id,
        });

      if (contributorError) {
        console.error('❌ Contributor relationship error:', contributorError);
        throw contributorError;
      }

      console.log('✅ Contributor relationship created successfully!');

      // Now add the admin role to the contributor
      console.log('📝 Adding admin role to contributor...');
      const { error: roleError } = await supabase
        .from("user_roles")
        .insert({
          user_id: newUserId,
          role: 'admin',
        });

      if (roleError) {
        console.error('❌ Role assignment error:', roleError);
        // Don't throw - contributor is created, just role assignment failed
        console.warn('Contributor created but admin role assignment failed');
      } else {
        console.log('✅ Admin role assigned successfully!');
      }

      return newUserId;
    },
    onSuccess: () => {
      toast({
        title: "Contributor created",
        description: "The contributor admin has been created successfully.",
      });
      setIsDialogOpen(false);
      setNewContributor({ email: "", password: "", firstName: "", lastName: "" });
      queryClient.invalidateQueries({ queryKey: ["contributors"] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete contributor mutation
  const deleteContributorMutation = useMutation({
    mutationFn: async (contributorId: string) => {
      // Remove the contributor relationship
      const { error } = await supabase
        .from("admin_contributors")
        .delete()
        .eq("contributor_admin_id", contributorId)
        .eq("parent_admin_id", user?.id);

      if (error) throw error;

      // Refresh the admin team table
      try {
        await supabase.functions.invoke("refresh-admin-team", {
          body: { admin_id: user?.id },
        });
      } catch (refreshError) {
        console.error("Error refreshing admin team:", refreshError);
      }
    },
    onSuccess: () => {
      toast({
        title: "Contributor removed",
        description: "The contributor has been removed successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["contributors"] });
      queryClient.invalidateQueries({ queryKey: ["admin-team"] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Contributors
            </CardTitle>
            <CardDescription>
              Manage contributor admins who share your permissions and limits
            </CardDescription>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <UserPlus className="h-4 w-4 mr-2" />
                Add Contributor
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>Create Contributor Admin</DialogTitle>
                <DialogDescription>
                  Create a new admin who will share your exact permissions and account limits
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    Contributors will have the same access and share your usage limits. They can manage users, teams, and all other resources you can access.
                  </AlertDescription>
                </Alert>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      value={newContributor.firstName}
                      onChange={(e) => setNewContributor({ ...newContributor, firstName: e.target.value })}
                      placeholder="John"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      value={newContributor.lastName}
                      onChange={(e) => setNewContributor({ ...newContributor, lastName: e.target.value })}
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={newContributor.email}
                    onChange={(e) => setNewContributor({ ...newContributor, email: e.target.value })}
                    placeholder="contributor@example.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={newContributor.password}
                    onChange={(e) => setNewContributor({ ...newContributor, password: e.target.value })}
                    placeholder="Minimum 8 characters"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    console.log('Create Contributor clicked', newContributor);
                    createContributorMutation.mutate();
                  }}
                  disabled={
                    createContributorMutation.isPending ||
                    !newContributor.email ||
                    !newContributor.password ||
                    !newContributor.firstName ||
                    !newContributor.lastName
                  }
                >
                  {createContributorMutation.isPending ? "Creating..." : "Create Contributor"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading contributors...</p>
        ) : contributors && contributors.length > 0 ? (
          <div className="space-y-2">
            {contributors.map((contributor) => (
              <Card key={contributor.id}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{contributor.profiles?.full_name || "No name"}</p>
                        <Badge variant="secondary" className="gap-1">
                          <Shield className="h-3 w-3" />
                          Contributor
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{contributor.profiles?.email}</p>
                      <p className="text-xs text-muted-foreground">
                        Added {new Date(contributor.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => deleteContributorMutation.mutate(contributor.contributor_admin_id)}
                      disabled={deleteContributorMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-6">
            <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-sm text-muted-foreground mb-4">
              No contributors yet. Add contributors to share your admin access.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
